import { formatDate } from "../../../../../util/time";
import { Modules } from "../../../../../util/constants";

const UNLIMITED_USERS_SENTINEL = 9999;

const toPositiveNumber = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) return null;
  return numericValue;
};

export const prettifyTitle = (title) => (title || "").replace(" - ", " - ");

export const toAmount = (item) => {
  if (typeof item?.amount === "number") return item.amount;
  if (typeof item?.amountMinor === "number") return item.amountMinor / 100;
  return 0;
};

const toTwoDigits = (value) => `${value || ""}`.padStart(2, "0");

const capitalizeFirst = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
};

const normalizePeriod = (value) => {
  const text = String(value || "").toLowerCase();
  if (text === "year" || text === "annual") return "annual";
  if (text === "month" || text === "monthly") return "monthly";
  return null;
};

const inferPeriodsFromLineItems = (lineItems = []) => {
  const periods = new Set();

  lineItems.forEach((item) => {
    const title = String(item?.title || "").toLowerCase();
    if (title.includes("annual")) periods.add("annual");
    if (title.includes("monthly")) periods.add("monthly");
  });

  return periods;
};

export const buildFallbackSummary = (subscription) => {
  const period = subscription?.plan?.period === "year" ? "Annual" : "Monthly";
  const planAmount =
    typeof subscription?.plan?.amount === "number"
      ? subscription.plan.amount / 100
      : 0;
  const moduleSubscriptions = subscription?.moduleSubscriptions || [];
  const moduleLineItems = moduleSubscriptions
    .filter((module) => module?.isActive || module?.isInTrial)
    .map((module) => ({
      id: module?.id || `module-${module?.moduleName || "plan"}`,
      title: module?.moduleName || "Module",
      quantity: 1,
      amount:
        typeof module?.amountMinor === "number" ? module.amountMinor / 100 : 0,
      children: [],
    }));

  const resolvedLineItems = moduleLineItems.length
    ? moduleLineItems
    : subscription?.plan
      ? [
          {
            id: "plan",
            title: subscription.plan.title || subscription.plan.name || "Plan",
            quantity: 1,
            amount: planAmount,
            children: [],
          },
        ]
      : [];

  const resolvedTotalAmount = resolvedLineItems.reduce(
    (total, item) =>
      total + (typeof item.amount === "number" ? item.amount : 0),
    0,
  );

  const status = subscription?.isSubscribed
    ? "Active"
    : subscription?.isInTrial
      ? "Trial"
      : "Inactive";

  return {
    status,
    commitmentTerm: period,
    billed: period,
    nextBillDate: subscription?.plan?.nextBillingDate || null,
    nextChargeDate: subscription?.plan?.nextBillingDate || null,
    currency: subscription?.billingCurrency,
    lineItems: resolvedLineItems,
    totalAmount: resolvedTotalAmount,
  };
};

export const getPaymentLabels = (paymentMethod) => {
  const cardMaskLabel = paymentMethod?.last4
    ? `•••• ${paymentMethod.last4}`
    : "";
  const cardBrand = capitalizeFirst(paymentMethod?.brand || "Card");
  const cardLabel = cardMaskLabel
    ? `${cardBrand} ${cardMaskLabel}`
    : "Card details available in billing portal";
  const expiryLabel =
    paymentMethod?.expiryMonth && paymentMethod?.expiryYear
      ? `Expires ${toTwoDigits(paymentMethod.expiryMonth)}/${paymentMethod.expiryYear}`
      : "";

  return {
    cardLabel,
    expiryLabel,
  };
};

export const getFormattedNextBillDate = (summary) => {
  const nextBillDate = summary?.nextBillDate || summary?.nextChargeDate;
  if (!nextBillDate) return "-";

  const parsedDate = new Date(nextBillDate);
  if (Number.isNaN(parsedDate.getTime())) return "-";

  return formatDate(parsedDate, { month: "short" });
};

export const getBillingCadenceMeta = (summary, moduleSubscriptions = []) => {
  const activePeriods = new Set(
    moduleSubscriptions
      .filter((module) => module?.isActive && !module?.isInTrial)
      .map((module) => normalizePeriod(module?.period))
      .filter(Boolean),
  );

  if (!activePeriods.size) {
    const lineItemPeriods = inferPeriodsFromLineItems(summary?.lineItems || []);
    lineItemPeriods.forEach((period) => activePeriods.add(period));
  }

  if (!activePeriods.size) {
    const fallback = normalizePeriod(
      summary?.billed || summary?.commitmentTerm,
    );
    if (fallback) activePeriods.add(fallback);
  }

  if (activePeriods.has("annual") && activePeriods.has("monthly")) {
    return {
      label: "Mixed",
      description: "Includes annual and monthly plans",
    };
  }

  if (activePeriods.has("annual")) {
    return {
      label: "Annual",
      description: "All active plans are annual",
    };
  }

  if (activePeriods.has("monthly")) {
    return {
      label: "Monthly",
      description: "All active plans are monthly",
    };
  }

  return {
    label: "-",
    description: "No active billing cadence",
  };
};

export const getIncludedUsersCount = (
  subscription,
  moduleSubscriptions = [],
) => {
  const activePaidModules = moduleSubscriptions.filter(
    (module) =>
      module?.isActive &&
      !module?.isInTrial &&
      String(module?.id || "") !== Modules.ORGANISATION,
  );

  const moduleLimits = activePaidModules
    .map((module) => toPositiveNumber(module?.addons?.users?.planLimit))
    .filter(Boolean);

  const hasReceivablesModuleLimit = activePaidModules.some(
    (module) =>
      String(module?.id || "") === Modules.RECEIVABLES &&
      toPositiveNumber(module?.addons?.users?.planLimit),
  );

  const planUsersLimit = toPositiveNumber(
    subscription?.plan?.addons?.users?.planLimit ||
      subscription?.plan?.features?.users,
  );

  const mergedLimits = [...moduleLimits];
  if (!hasReceivablesModuleLimit && planUsersLimit) {
    mergedLimits.push(planUsersLimit);
  }

  if (!mergedLimits.length) return null;
  if (mergedLimits.some((limit) => limit >= UNLIMITED_USERS_SENTINEL)) {
    return UNLIMITED_USERS_SENTINEL;
  }

  return mergedLimits.reduce((total, limit) => total + limit, 0);
};

export const formatIncludedUsersLabel = (count) => {
  const normalizedCount = toPositiveNumber(count);
  if (!normalizedCount) return "-";
  if (normalizedCount >= UNLIMITED_USERS_SENTINEL) return "Unlimited users";
  return `${normalizedCount} ${normalizedCount === 1 ? "user" : "users"}`;
};

export const getModuleIncludedUsersCount = (
  subscription,
  moduleSubscription,
) => {
  if (!moduleSubscription) return null;

  const perModuleIncludedUsers = Array.isArray(
    moduleSubscription?.addons?.users?.includedUsersByPlan,
  )
    ? moduleSubscription.addons.users.includedUsersByPlan.find(
        (entry) =>
          String(entry?.moduleId || "") ===
          String(moduleSubscription?.id || ""),
      )?.includedUsers
    : null;
  const resolvedPerModuleIncludedUsers = toPositiveNumber(
    perModuleIncludedUsers,
  );
  if (resolvedPerModuleIncludedUsers) {
    return resolvedPerModuleIncludedUsers;
  }

  const moduleUsersPlanLimit = toPositiveNumber(
    moduleSubscription?.addons?.users?.planLimit,
  );
  if (moduleUsersPlanLimit) {
    return moduleUsersPlanLimit;
  }

  if (String(moduleSubscription?.id || "") === Modules.RECEIVABLES) {
    return toPositiveNumber(
      subscription?.plan?.addons?.users?.planLimit ||
        subscription?.plan?.features?.users,
    );
  }

  return null;
};

export const formatIncludedUsersBreakdownLabel = (count) => {
  const normalizedCount = toPositiveNumber(count);
  if (!normalizedCount) return null;

  if (normalizedCount >= UNLIMITED_USERS_SENTINEL) {
    return "Included users x Unlimited";
  }

  return `Included users x ${normalizedCount}`;
};
